/**
 * FikoSPHParametreler.ts
 *
 * SPH Sıvı Simülasyon Sistemi - Parametre ve Tip Tanımları
 *
 * Bu dosya WebGPU tabanlı SPH (Smoothed Particle Hydrodynamics) simülasyon
 * sisteminin TÜM tip, enum ve parametre yapılarını tanımlar.
 *
 * ÖNEMLI: Bu saf bir tip/data dosyasıdır:
 * - GPU kodu yok
 * - Three.js import yok
 * - Sadece TypeScript tip tanımları ve veri yapıları
 * - strict: false (proje kuralı)
 */

// ============================================================================
// 1. TEMEL TİPLER
// ============================================================================

/**
 * 3D vektör (Three.js bağımlılığı olmadan)
 */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * RGBA renk
 */
export interface Renk {
  r: number;  // 0-1
  g: number;  // 0-1
  b: number;  // 0-1
  a: number;  // 0-1
}

/**
 * Parametre tanım metadata'sı
 * UI kontrollerinde min/max/step/unit gösterimi için
 */
export interface ParametreTanimi<T> {
  deger: T;
  varsayilan: T;
  min?: number;       // numeric parametreler için
  max?: number;
  adim?: number;      // UI slider için adım
  birim?: string;     // "m/s", "Pa", "°C" gibi birim
  aciklama?: string;  // tooltip açıklaması
}

// ============================================================================
// 2. ENUM TANIMLARI
// ============================================================================

/**
 * SPH solver algoritma tipleri
 */
export enum SolverTipi {
  DFSPH = 'DFSPH',      // Divergence-Free SPH (en hassas)
  PBF = 'PBF',          // Position-Based Fluids (en hızlı)
  PCISPH = 'PCISPH'     // Predictive-Corrective Incompressible SPH
}

/**
 * Kalite seviye presetleri
 */
export enum KaliteSeviyesi {
  DUSUK = 'dusuk',      // 7.5K particle/L, ~60 FPS
  ORTA = 'orta',        // 30K particle/L, ~60 FPS
  YUKSEK = 'yuksek',    // 80K particle/L, ~60 FPS
  ULTRA = 'ultra',      // 280K particle/L, ~30 FPS
  MANUEL = 'manuel'     // Kullanıcı tanımlı
}

/**
 * Döküm girişi akış profili
 */
export enum AkisProfili {
  SABIT = 'sabit',      // Sabit debi
  RAMPA = 'rampa',      // Zaman içinde artış
  SINUS = 'sinus',      // Sinüzoidal
  PULSE = 'pulse',      // Darbe (on/off)
  OZEL = 'ozel'         // Özel fonksiyon
}

/**
 * Giriş kesit şekli
 */
export enum KesitSekli {
  DAIRE = 'daire',
  DIKDORTGEN = 'dikdortgen',
  SLOT = 'slot',
  OZEL = 'ozel'
}

/**
 * Türbülans profili
 */
export enum TurbulansProfili {
  LAMINAR = 'laminar',
  TURBULANT = 'turbulant',
  PROFIL = 'profil'       // Özel hız profili
}

/**
 * Hava deliği tipleri
 */
export enum HavaDeligiTipi {
  ACIK = 'acik',          // Açık delik (serbest akış)
  FILTRELI = 'filtreli',  // Filtreli (akış direnci var)
  TEK_YONLU = 'tek_yonlu' // Tek yönlü valf
}

/**
 * Engel geometri tipleri
 */
export enum EngelTipi {
  DUZ_PLAKA = 'duz_plaka',
  EGRILI = 'egrili',
  SILINDIR = 'silindir',
  OZEL_MESH = 'ozel_mesh'
}

/**
 * Engel hareket tipleri
 */
export enum EngelHareket {
  STATIK = 'statik',
  DONUYOR = 'donuyor',
  SALINIYOR = 'saliniyor'
}

/**
 * Çıkış kontrol tipleri
 */
export enum CikisTipi {
  SERBEST = 'serbest',                // Serbest akış
  BASINC_KONTROL = 'basinc_kontrol',  // Basınç hedefi
  SEVIYE_KONTROL = 'seviye_kontrol'   // Seviye hedefi
}

/**
 * Besleyici geometri şekilleri
 */
export enum BesleyiciSekil {
  SILINDIR = 'silindir',
  KURE = 'kure',
  DIKDORTGEN = 'dikdortgen'
}

/**
 * Sanal sensör ölçüm tipleri
 */
export enum SensorTipi {
  BASINC = 'basinc',
  HIZ = 'hiz',
  SICAKLIK = 'sicaklik',
  DOLUM = 'dolum',          // Dolum oranı %
  DEBI = 'debi',            // Akış hızı
  YOGUNLUK = 'yogunluk',
  TURBLANS = 'turblans'
}

/**
 * Termal eleman tipleri
 */
export enum TermalTipi {
  SOGUTUCU = 'sogutucu',
  ISITICI = 'isitici',
  SABIT_SICAKLIK = 'sabit_sicaklik'
}

/**
 * Render modları
 */
export enum RenderModu {
  PARTICLE = 'particle',  // Ham particle render
  FLUID = 'fluid',        // Screen-space fluid
  HIBRIT = 'hibrit'       // İkisi karışık
}

/**
 * Heatmap overlay tipleri
 */
export enum HeatmapTipi {
  YOK = 'yok',
  HIZ = 'hiz',
  BASINC = 'basinc',
  SICAKLIK = 'sicaklik',
  YOGUNLUK = 'yogunluk',
  YAS = 'yas',                  // Particle yaşı
  KAYNAK = 'kaynak',            // Hangi girişten
  TURBLANS = 'turblans',
  DOLUM_SIRASI = 'dolum_sirasi' // Doldurma sırası
}

/**
 * Eleman yerleştirme modları
 */
export enum YerlestirmeModu {
  TIKLA = 'tikla',          // 3D sahne üzerine tıkla
  MANUEL = 'manuel',        // Manuel XYZ gir
  MESH_BAGLA = 'mesh_bagla' // Mesh yüzeyine yapıştır
}

/**
 * Particle faz durumu (termal sistem için)
 */
export enum ParticleFaz {
  SIVI = 0,
  KATI = 1,
  GAZ = 2
}

// ============================================================================
// 3. GLOBAL SPH PARAMETRELERİ
// ============================================================================

/**
 * SPH simülasyon global parametreleri
 */
export interface SPHGlobalParametreler {
  // Solver seçimi
  solverTipi: SolverTipi;

  // Fiziksel parametreler
  gravity: Vec3;              // Yerçekimi vektörü (m/s²)
  viscosity: number;          // Viskozite (Pa·s), 0.0001 - 100
  surfaceTension: number;     // Yüzey gerilimi (N/m), 0 - 1
  restDensity: number;        // Dinlenme yoğunluğu (kg/m³), 100 - 20000
  boundaryDamping: number;    // Sınır sönümleme, 0 - 1

  // Zaman adımı
  dt: number;                 // Hedef zaman adımı (s), 0.0001 - 0.02
  maxDt: number;              // Maksimum zaman adımı (s), 0.001 - 0.05
  cflFactor: number;          // CFL güvenlik faktörü, 0.1 - 1.0

  // Kalite ve limitler
  kalite: KaliteSeviyesi;     // Kalite preset
  maxParticle: number;        // Maksimum particle sayısı, 1000 - 2000000
  simHizi: number;            // Simülasyon hız çarpanı, 0.25 - 4.0

  // SPH kernel ve parçacık
  smoothingRadius: number;    // SPH kernel yarıçapı h (m), kaliteye bağlı
  particleRadius: number;     // Görsel parçacık yarıçapı (m)

  // Simülasyon alanı sınırları
  simBounds: { min: Vec3; max: Vec3 };

  // Ek özellikler
  termalAktif: boolean;       // Termal simülasyon aktif mi
  gazAktif: boolean;          // Gaz/kabarcık simülasyonu aktif mi
  havaBasinci: number;        // Atmosfer basıncı (Pa)
}

// ============================================================================
// 4. SOLVER-SPECIFIC PARAMETRELERİ
// ============================================================================

/**
 * DFSPH (Divergence-Free SPH) parametreleri
 */
export interface DFSPHParametreler {
  divFreeIter: number;      // Divergence-free iterasyon, 1-10
  constDensIter: number;    // Constant density iterasyon, 1-10
  densityError: number;     // Yoğunluk hata toleransı, 0.0001-0.1
}

/**
 * PBF (Position-Based Fluids) parametreleri
 */
export interface PBFParametreler {
  pbfIter: number;          // PBF iterasyon sayısı, 1-10
  pbfEpsilon: number;       // Gevşetme epsilon, 1-10000
  pbfSCorr_k: number;       // Tensile instability k, 0-1
  pbfSCorr_n: number;       // Tensile instability n, 1-10
  xsphC: number;            // XSPH smoothing c, 0-0.1
}

/**
 * PCISPH (Predictive-Corrective Incompressible SPH) parametreleri
 */
export interface PCISPHParametreler {
  pcisphIter: number;       // Maksimum iterasyon, 1-10
  pcisphMinIter: number;    // Minimum iterasyon, 1-5
  pcisphMaxError: number;   // Maksimum yoğunluk hatası, 0.001-0.1
}

// ============================================================================
// 5. TERMAL SİSTEM PARAMETRELERİ
// ============================================================================

/**
 * Termal simülasyon parametreleri
 */
export interface TermalParametreler {
  termalAktif: boolean;

  // Ortam ve malzeme
  ortamSicakligi: number;       // Ortam sıcaklığı (°C), -50 - 2000
  difuzyonKatsayisi: number;    // Isı difüzyonu (m²/s), 0 - 0.01

  // Faz değişimi
  solidusSicaklik: number;      // Solidus sıcaklığı (°C), -50 - 2000
  liquidusSicaklik: number;     // Liquidus sıcaklığı (°C), -50 - 2000
  donmaLatantIsisi: number;     // Latent ısı (J/kg), 0 - 1000000
}

// ============================================================================
// 6. GAZ/KABARCIK PARAMETRELERİ
// ============================================================================

/**
 * Gaz/kabarcık simülasyon parametreleri
 */
export interface GazParametreler {
  gazAktif: boolean;

  kabarcikOlusumEsigi: number;  // Oluşum basınç eşiği (Pa)
  kabarcikMinBoyut: number;     // Min kabarcık boyutu (m), 0.0005
  kabarcikMaxBoyut: number;     // Max kabarcık boyutu (m), 0.01
  yukselmeHizi: number;         // Yükselme hızı faktörü, 0-10
  birlesme: boolean;            // Kabarcık birleşmesi aktif
  patlamaEsigi: number;         // Patlama boyutu eşiği (m)
}

// ============================================================================
// 7. RENDER PARAMETRELERİ
// ============================================================================

/**
 * Görselleştirme parametreleri
 */
export interface RenderParametreler {
  renderModu: RenderModu;

  // Particle rendering
  particleBoyutu: number;       // Particle boyutu çarpanı, 0.5-5.0

  // Screen-space fluid
  blurKernelSize: number;       // Blur kernel boyutu, 5-25
  blurDepthFalloff: number;     // Depth falloff, 0.1-10.0

  // Materyal
  saydamlik: number;            // Şeffaflık, 0-1
  kirilmaIndisi: number;        // Kırılma indisi, 1.0-2.0
  absorpsiyon: number;          // Işık absorpsiyonu, 0-5
  spekuler: number;             // Specular highlight, 0-1

  // Overlay
  heatmapTipi: HeatmapTipi;

  // Eleman ikonları
  elemanIkonGoster: boolean;
  elemanIkonBoyutu: number;     // İkon boyutu çarpanı, 0.5-3.0
}

// ============================================================================
// 8. DÖKÜM GİRİŞİ (INLET) KONFIGURASYON
// ============================================================================

/**
 * Döküm girişi tam konfigürasyonu (GPU: 128 bytes)
 */
export interface DokumGirisiConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Pozisyon ve yönelim
  pozisyon: Vec3;
  yon: Vec3;                    // Normalize vektör

  // Akış parametreleri
  hiz: number;                  // m/s, 0.01 - 100
  debi: number;                 // particle/s, 10 - 50000

  // Geometri
  yaricap: number;              // m, 0.001 - 1.0
  kesitSekli: KesitSekli;
  kesitBoyut: { w: number; h: number }; // Dikdörtgen için

  // Limitler
  maxMesafe: number;            // m, 0.01 - 100, Infinity için çok büyük değer
  maxOmur: number;              // s, 0.1 - 3600, Infinity için çok büyük değer

  // Malzeme
  malzemeId: string;            // Malzeme ID referansı
  sicaklik: number;             // °C, -50 - 2000

  // Türbülans
  turbulansProfili: TurbulansProfili;
  turbulansYogunluk: number;    // 0 - 1

  // Akış profili
  akisProfili: AkisProfili;
  akisBaslangic: number;        // s, akış başlangıç zamanı
  akisBitis: number;            // s, akış bitiş zamanı

  // Rampa parametreleri (RAMPA profili için)
  rampaYukselis: number;        // s, 0 - 60

  // Pulse parametreleri (PULSE profili için)
  pulsePeriyot: number;         // s, 0.1 - 60
  pulseDuty: number;            // %, 10 - 90

  // Görsel
  renk: Renk;
}

// ============================================================================
// 9. HAVA DELİĞİ (AIR VENT) KONFIGURASYON
// ============================================================================

/**
 * Hava deliği konfigürasyonu (GPU: 64 bytes)
 */
export interface HavaDeligiConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Pozisyon
  pozisyon: Vec3;
  yon: Vec3;

  // Geometri
  cap: number;                  // m, 0.0005 - 0.1
  derinlik: number;             // m, 0.001 - 0.05

  // Akış parametreleri
  geriBasinc: number;           // Pa, 0 - 100000
  akisDirenci: number;          // 0 - 1

  // Tip ve davranış
  tipi: HavaDeligiTipi;
  gecirgenlik: number;          // 0 - 1 (FILTRELI için)

  // Zamanlama
  acilmaZamani: number;         // s
  kapanmaZamani: number;        // s (Infinity için çok büyük)
}

// ============================================================================
// 10. BESLEYİCİ (FEEDER) KONFIGURASYON
// ============================================================================

/**
 * Besleyici konfigürasyonu (GPU: 64 bytes)
 */
export interface BesleyiciConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Pozisyon
  pozisyon: Vec3;

  // Geometri
  sekil: BesleyiciSekil;
  boyutlar: {
    cap?: number;               // SILINDIR, KURE için
    yukseklik?: number;         // SILINDIR, DIKDORTGEN için
    genislik?: number;          // DIKDORTGEN için
    derinlik?: number;          // DIKDORTGEN için
  };

  // Hesaplanan
  hacim: number;                // m³ (auto-calculated)

  // Bağlantı
  baglantiBoyutu: number;       // m, 0.005 - 0.1
  oncelik: number;              // 1 - 10 (düşük = önce doldurulur)
}

// ============================================================================
// 11. FİLTRE KONFIGURASYON
// ============================================================================

/**
 * Filtre konfigürasyonu (GPU: 64 bytes)
 */
export interface FiltreConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Pozisyon
  pozisyon: Vec3;
  yon: Vec3;

  // Geometri
  boyut: {
    genislik: number;           // m
    yukseklik: number;          // m
  };

  // Filtre özellikleri
  gozenekBoyutu: number;        // m, 0.0001 - 0.01
  gecirgenlik: number;          // 0 - 1
  basincDususu: number;         // Pa, 0 - 10000
  kapasite: number;             // particle count, 0 - Infinity
}

// ============================================================================
// 12. ENGEL (BARRIER) KONFIGURASYON
// ============================================================================

/**
 * Engel konfigürasyonu (GPU: 128 bytes)
 */
export interface EngelConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Tip
  tipi: EngelTipi;

  // Pozisyon
  pozisyon: Vec3;
  yon: Vec3;

  // Geometri
  boyut: {
    genislik: number;           // m
    yukseklik: number;          // m
  };
  kalinlik: number;             // m, 0.001 - 0.05

  // Akış özellikleri
  gecirgenlik: number;          // 0 - 1
  surukleme: number;            // 0 - 1

  // Hareket
  hareketli: EngelHareket;
  donusHizi: number;            // rpm, 0 - 1000
  salınımGenlik: number;        // m, 0 - 0.5
  salınımFrekans: number;       // Hz, 0 - 100
}

// ============================================================================
// 13. ÇIKIŞ (OUTLET) KONFIGURASYON
// ============================================================================

/**
 * Çıkış konfigürasyonu (GPU: 64 bytes)
 */
export interface CikisConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Pozisyon
  pozisyon: Vec3;
  yon: Vec3;

  // Geometri
  cap: number;                  // m, 0.001 - 0.5

  // Kontrol
  tipi: CikisTipi;
  hedefBasinc: number;          // Pa, 0 - 100000
  hedefSeviye: number;          // m (Y-coordinate)

  // Zamanlama
  acilmaZamani: number;         // s
}

// ============================================================================
// 14. SANAL SENSÖR KONFIGURASYON
// ============================================================================

/**
 * Sanal sensör konfigürasyonu (GPU: 32 bytes)
 */
export interface SanalSensorConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Tip
  tipi: SensorTipi;

  // Pozisyon
  pozisyon: Vec3;
  olcumYaricap: number;         // m, 0.001 - 0.1

  // Örnekleme
  orneklemeFrekansi: number;    // Hz, 1 - 60

  // Çıktı
  grafikGoster: boolean;
  kaydet: boolean;
  alarmEsigi?: number;          // Opsiyonel alarm eşiği
}

// ============================================================================
// 15. TERMAL ELEMAN KONFIGURASYON
// ============================================================================

/**
 * Termal eleman konfigürasyonu (GPU: 64 bytes)
 */
export interface TermalElemanConfig {
  // Temel
  id: string;
  ad: string;
  aktif: boolean;
  gorunur: boolean;

  // Tip
  tipi: TermalTipi;

  // Pozisyon
  pozisyon: Vec3;
  etkiAlaniCap: number;         // m, 0.01 - 1

  // Termal özellikler
  hedefSicaklik: number;        // °C, -50 - 2000
  transferKatsayisi: number;    // W/(m²·K), 0 - 10000
}

// ============================================================================
// 16. ZAMAN ÇİZGİSİ / SENARYO
// ============================================================================

/**
 * Zaman keyframe
 */
export interface ZamanKeyframe {
  zaman: number;                // s
  elemanId: string;
  parametre: string;            // Parametre adı (ör: "hiz", "sicaklik")
  deger: any;                   // Keyframe değeri
}

/**
 * Döküm senaryosu
 */
export interface DokumSenaryo {
  ad: string;
  aciklama: string;
  keyframeler: ZamanKeyframe[];
  toplamSure: number;           // s
}

// ============================================================================
// 17. KOMPLE SİMÜLASYON KONFİGÜRASYONU
// ============================================================================

/**
 * Tüm SPH simülasyon konfigürasyonu
 */
export interface SPHSimulasyonConfig {
  // Global parametreler
  global: SPHGlobalParametreler;

  // Solver parametreleri
  dfsph: DFSPHParametreler;
  pbf: PBFParametreler;
  pcisph: PCISPHParametreler;

  // Özellik parametreleri
  termal: TermalParametreler;
  gaz: GazParametreler;
  render: RenderParametreler;

  // Eleman listesi
  dokumGirisleri: DokumGirisiConfig[];
  havaDelikleri: HavaDeligiConfig[];
  besleyiciler: BesleyiciConfig[];
  filtreler: FiltreConfig[];
  engeller: EngelConfig[];
  cikislar: CikisConfig[];
  sensorler: SanalSensorConfig[];
  termalElemanlar: TermalElemanConfig[];

  // Senaryo
  senaryo: DokumSenaryo | null;
}

// ============================================================================
// 18. KALİTE PRESETLERI
// ============================================================================

/**
 * Kalite preset tanımı
 */
export interface KalitePreset {
  particleBoyutu: number;   // Particle boyutu (m)
  litreBasi: number;        // Litre başına particle
  gpuMemory: number;        // MB/litre GPU bellek
  hedefFPS: number;         // Hedef FPS
}

/**
 * Kalite preset değerleri
 */
export const KALITE_PRESETLERI: Record<KaliteSeviyesi, KalitePreset> = {
  [KaliteSeviyesi.DUSUK]: {
    particleBoyutu: 0.004,
    litreBasi: 7500,
    gpuMemory: 8,
    hedefFPS: 60
  },
  [KaliteSeviyesi.ORTA]: {
    particleBoyutu: 0.0025,
    litreBasi: 30000,
    gpuMemory: 25,
    hedefFPS: 60
  },
  [KaliteSeviyesi.YUKSEK]: {
    particleBoyutu: 0.0018,
    litreBasi: 80000,
    gpuMemory: 65,
    hedefFPS: 60
  },
  [KaliteSeviyesi.ULTRA]: {
    particleBoyutu: 0.0012,
    litreBasi: 280000,
    gpuMemory: 220,
    hedefFPS: 30
  },
  [KaliteSeviyesi.MANUEL]: {
    particleBoyutu: 0.002,
    litreBasi: 0,
    gpuMemory: 0,
    hedefFPS: 60
  }
};

// ============================================================================
// 19. GPU STRUCT BOYUTLARI
// ============================================================================

/**
 * GPU buffer struct boyutları (bytes)
 */
export const GPU_STRUCT_BOYUTLARI = {
  // Eleman boyutları
  DOKUM_GIRISI: 128,
  HAVA_DELIGI: 64,
  BESLEYICI: 64,
  FILTRE: 64,
  ENGEL: 128,
  CIKIS: 64,
  SENSOR: 32,
  TERMAL_ELEMAN: 64,

  // Particle data
  PARTICLE_AGNOSTIK: 64,    // pos + vel + density + force

  // Maksimum eleman sayıları
  MAX_GIRIS: 64,
  MAX_DELIK: 64,
  MAX_BESLEYICI: 16,
  MAX_FILTRE: 32,
  MAX_ENGEL: 32,
  MAX_CIKIS: 32,
  MAX_SENSOR: 64,
  MAX_TERMAL: 32,
} as const;

// ============================================================================
// 20. ADAPTİF PARTICLE HESAPLAMA
// ============================================================================

/**
 * Hacme göre particle sayısını hesapla
 */
export function hesaplaParticleSayisi(hacimM3: number, kalite: KaliteSeviyesi): number {
  const preset = KALITE_PRESETLERI[kalite];
  if (kalite === KaliteSeviyesi.MANUEL) {
    return 0; // Manuel modda kullanıcı belirler
  }

  const hacimLitre = hacimM3 * 1000;
  return Math.floor(hacimLitre * preset.litreBasi);
}

/**
 * Kaliteye göre particle boyutunu hesapla
 */
export function hesaplaParticleBoyutu(kalite: KaliteSeviyesi): number {
  return KALITE_PRESETLERI[kalite].particleBoyutu;
}

/**
 * GPU bellek ihtiyacını tahmin et (MB)
 */
export function tahminGPUBellek(particleSayisi: number): number {
  const bytesPerParticle = GPU_STRUCT_BOYUTLARI.PARTICLE_AGNOSTIK;
  const totalBytes = particleSayisi * bytesPerParticle * 3; // 3 buffer (current, prev, temp)
  return totalBytes / (1024 * 1024);
}

// ============================================================================
// 21. VARSAYILAN DEĞERLER - GLOBAL
// ============================================================================

/**
 * Varsayılan global SPH parametreleri
 */
export function varsayilanGlobalParametreler(): SPHGlobalParametreler {
  return {
    solverTipi: SolverTipi.DFSPH,
    gravity: { x: 0, y: -9.81, z: 0 },
    viscosity: 0.001,
    surfaceTension: 0.0728,
    restDensity: 1000,
    boundaryDamping: 0.3,
    dt: 0.005,
    maxDt: 0.01,
    cflFactor: 0.4,
    kalite: KaliteSeviyesi.ORTA,
    maxParticle: 100000,
    simHizi: 1.0,
    termalAktif: false,
    gazAktif: false,
    havaBasinci: 101325,
    smoothingRadius: 0.04,
    particleRadius: 0.015,
    simBounds: {
      min: { x: -1, y: -1, z: -1 },
      max: { x: 1, y: 1, z: 1 }
    }
  };
}

/**
 * Varsayılan DFSPH parametreleri
 */
export function varsayilanDFSPHParametreler(): DFSPHParametreler {
  return {
    divFreeIter: 2,
    constDensIter: 3,
    densityError: 0.001
  };
}

/**
 * Varsayılan PBF parametreleri
 */
export function varsayilanPBFParametreler(): PBFParametreler {
  return {
    pbfIter: 3,
    pbfEpsilon: 100,
    pbfSCorr_k: 0.1,
    pbfSCorr_n: 4,
    xsphC: 0.01
  };
}

/**
 * Varsayılan PCISPH parametreleri
 */
export function varsayilanPCISPHParametreler(): PCISPHParametreler {
  return {
    pcisphIter: 3,
    pcisphMinIter: 2,
    pcisphMaxError: 0.01
  };
}

/**
 * Varsayılan termal parametreler
 */
export function varsayilanTermalParametreler(): TermalParametreler {
  return {
    termalAktif: false,
    ortamSicakligi: 20,
    difuzyonKatsayisi: 1.43e-7,
    solidusSicaklik: 0,
    liquidusSicaklik: 100,
    donmaLatantIsisi: 334000
  };
}

/**
 * Varsayılan gaz parametreleri
 */
export function varsayilanGazParametreler(): GazParametreler {
  return {
    gazAktif: false,
    kabarcikOlusumEsigi: 102000,
    kabarcikMinBoyut: 0.0005,
    kabarcikMaxBoyut: 0.01,
    yukselmeHizi: 1.0,
    birlesme: true,
    patlamaEsigi: 0.002
  };
}

/**
 * Varsayılan render parametreleri
 */
export function varsayilanRenderParametreler(): RenderParametreler {
  return {
    renderModu: RenderModu.FLUID,
    particleBoyutu: 1.5,
    blurKernelSize: 11,
    blurDepthFalloff: 2.0,
    saydamlik: 0.8,
    kirilmaIndisi: 1.33,
    absorpsiyon: 0.5,
    spekuler: 0.5,
    heatmapTipi: HeatmapTipi.YOK,
    elemanIkonGoster: true,
    elemanIkonBoyutu: 1.0
  };
}

// ============================================================================
// 22. VARSAYILAN DEĞERLER - ELEMANLAR
// ============================================================================

/**
 * Varsayılan döküm girişi
 */
export function varsayilanDokumGirisi(id?: string): DokumGirisiConfig {
  return {
    id: id || `giris_${Date.now()}`,
    ad: 'Yeni Giriş',
    aktif: true,
    gorunur: true,
    pozisyon: { x: 0, y: 0, z: 0 },
    yon: { x: 0, y: -1, z: 0 },
    hiz: 2.0,
    debi: 1000,
    yaricap: 0.02,
    kesitSekli: KesitSekli.DAIRE,
    kesitBoyut: { w: 0.02, h: 0.02 },
    maxMesafe: 999999,
    maxOmur: 999999,
    malzemeId: 'default',
    sicaklik: 20,
    turbulansProfili: TurbulansProfili.LAMINAR,
    turbulansYogunluk: 0.05,
    akisProfili: AkisProfili.SABIT,
    akisBaslangic: 0,
    akisBitis: 999999,
    rampaYukselis: 1.0,
    pulsePeriyot: 1.0,
    pulseDuty: 50,
    renk: { r: 0.2, g: 0.6, b: 1.0, a: 1.0 }
  };
}

/**
 * Varsayılan hava deliği
 */
export function varsayilanHavaDeligi(id?: string): HavaDeligiConfig {
  return {
    id: id || `delik_${Date.now()}`,
    ad: 'Yeni Hava Deliği',
    aktif: true,
    gorunur: true,
    pozisyon: { x: 0, y: 0, z: 0 },
    yon: { x: 0, y: 1, z: 0 },
    cap: 0.005,
    derinlik: 0.01,
    geriBasinc: 101325,
    akisDirenci: 0.1,
    tipi: HavaDeligiTipi.ACIK,
    gecirgenlik: 0,
    acilmaZamani: 0,
    kapanmaZamani: 999999
  };
}

/**
 * Varsayılan besleyici
 */
export function varsayilanBesleyici(id?: string): BesleyiciConfig {
  return {
    id: id || `besleyici_${Date.now()}`,
    ad: 'Yeni Besleyici',
    aktif: true,
    gorunur: true,
    pozisyon: { x: 0, y: 0, z: 0 },
    sekil: BesleyiciSekil.SILINDIR,
    boyutlar: {
      cap: 0.05,
      yukseklik: 0.1
    },
    hacim: Math.PI * 0.05 * 0.05 * 0.1,
    baglantiBoyutu: 0.02,
    oncelik: 5
  };
}

/**
 * Varsayılan filtre
 */
export function varsayilanFiltre(id?: string): FiltreConfig {
  return {
    id: id || `filtre_${Date.now()}`,
    ad: 'Yeni Filtre',
    aktif: true,
    gorunur: true,
    pozisyon: { x: 0, y: 0, z: 0 },
    yon: { x: 0, y: 1, z: 0 },
    boyut: {
      genislik: 0.05,
      yukseklik: 0.05
    },
    gozenekBoyutu: 0.002,
    gecirgenlik: 0.5,
    basincDususu: 100,
    kapasite: 999999
  };
}

/**
 * Varsayılan engel
 */
export function varsayilanEngel(id?: string): EngelConfig {
  return {
    id: id || `engel_${Date.now()}`,
    ad: 'Yeni Engel',
    aktif: true,
    gorunur: true,
    tipi: EngelTipi.DUZ_PLAKA,
    pozisyon: { x: 0, y: 0, z: 0 },
    yon: { x: 0, y: 1, z: 0 },
    boyut: {
      genislik: 0.1,
      yukseklik: 0.1
    },
    kalinlik: 0.005,
    gecirgenlik: 0,
    surukleme: 0.5,
    hareketli: EngelHareket.STATIK,
    donusHizi: 0,
    salınımGenlik: 0,
    salınımFrekans: 0
  };
}

/**
 * Varsayılan çıkış
 */
export function varsayilanCikis(id?: string): CikisConfig {
  return {
    id: id || `cikis_${Date.now()}`,
    ad: 'Yeni Çıkış',
    aktif: true,
    gorunur: true,
    pozisyon: { x: 0, y: 0, z: 0 },
    yon: { x: 0, y: -1, z: 0 },
    cap: 0.02,
    tipi: CikisTipi.SERBEST,
    hedefBasinc: 0,
    hedefSeviye: 0,
    acilmaZamani: 0
  };
}

/**
 * Varsayılan sanal sensör
 */
export function varsayilanSanalSensor(id?: string): SanalSensorConfig {
  return {
    id: id || `sensor_${Date.now()}`,
    ad: 'Yeni Sensör',
    aktif: true,
    gorunur: true,
    tipi: SensorTipi.BASINC,
    pozisyon: { x: 0, y: 0, z: 0 },
    olcumYaricap: 0.01,
    orneklemeFrekansi: 1,
    grafikGoster: true,
    kaydet: false,
    alarmEsigi: undefined
  };
}

/**
 * Varsayılan termal eleman
 */
export function varsayilanTermalEleman(id?: string): TermalElemanConfig {
  return {
    id: id || `termal_${Date.now()}`,
    ad: 'Yeni Termal Eleman',
    aktif: true,
    gorunur: true,
    tipi: TermalTipi.SOGUTUCU,
    pozisyon: { x: 0, y: 0, z: 0 },
    etkiAlaniCap: 0.1,
    hedefSicaklik: 100,
    transferKatsayisi: 100
  };
}

/**
 * Varsayılan senaryo
 */
export function varsayilanSenaryo(): DokumSenaryo {
  return {
    ad: 'Yeni Senaryo',
    aciklama: '',
    keyframeler: [],
    toplamSure: 0
  };
}

// ============================================================================
// 23. KOMPLE KONFİGURASYON FACTORY
// ============================================================================

/**
 * Varsayılan SPH simülasyon konfigürasyonu
 */
export function varsayilanSPHConfig(): SPHSimulasyonConfig {
  return {
    global: varsayilanGlobalParametreler(),
    dfsph: varsayilanDFSPHParametreler(),
    pbf: varsayilanPBFParametreler(),
    pcisph: varsayilanPCISPHParametreler(),
    termal: varsayilanTermalParametreler(),
    gaz: varsayilanGazParametreler(),
    render: varsayilanRenderParametreler(),
    dokumGirisleri: [],
    havaDelikleri: [],
    besleyiciler: [],
    filtreler: [],
    engeller: [],
    cikislar: [],
    sensorler: [],
    termalElemanlar: [],
    senaryo: null
  };
}

// ============================================================================
// 24. YARDIMCI TİP KORUMASI FONKSİYONLARI
// ============================================================================

/**
 * Vec3 oluştur
 */
export function vec3(x: number = 0, y: number = 0, z: number = 0): Vec3 {
  return { x, y, z };
}

/**
 * Renk oluştur
 */
export function renk(r: number = 0, g: number = 0, b: number = 0, a: number = 1): Renk {
  return { r, g, b, a };
}

/**
 * Vec3 normalize
 */
export function normalizeVec3(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  if (len === 0) return { x: 0, y: 1, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

/**
 * Vec3 kopyala
 */
export function cloneVec3(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z };
}

/**
 * Renk kopyala
 */
export function cloneRenk(c: Renk): Renk {
  return { r: c.r, g: c.g, b: c.b, a: c.a };
}

/**
 * Parametre değer clamp
 */
export function clampDeger(deger: number, min?: number, max?: number): number {
  if (min !== undefined && deger < min) return min;
  if (max !== undefined && deger > max) return max;
  return deger;
}

// ============================================================================
// 25. VALIDASYON FONKSİYONLARI
// ============================================================================

/**
 * Config validasyon sonucu
 */
export interface ValidationSonuc {
  gecerli: boolean;
  hatalar: string[];
  uyarilar: string[];
}

/**
 * Global parametreleri validate et
 */
export function validateGlobalParametreler(params: SPHGlobalParametreler): ValidationSonuc {
  const sonuc: ValidationSonuc = { gecerli: true, hatalar: [], uyarilar: [] };

  if (params.viscosity < 0.0001 || params.viscosity > 100) {
    sonuc.hatalar.push('Viskozite 0.0001 - 100 arasında olmalı');
    sonuc.gecerli = false;
  }

  if (params.restDensity < 100 || params.restDensity > 20000) {
    sonuc.hatalar.push('Yoğunluk 100 - 20000 arasında olmalı');
    sonuc.gecerli = false;
  }

  if (params.dt < 0.0001 || params.dt > 0.02) {
    sonuc.hatalar.push('Zaman adımı 0.0001 - 0.02 arasında olmalı');
    sonuc.gecerli = false;
  }

  if (params.maxParticle > 2000000) {
    sonuc.uyarilar.push('2M üzeri particle GPU belleği tüketebilir');
  }

  return sonuc;
}

/**
 * Döküm girişini validate et
 */
export function validateDokumGirisi(giris: DokumGirisiConfig): ValidationSonuc {
  const sonuc: ValidationSonuc = { gecerli: true, hatalar: [], uyarilar: [] };

  if (giris.hiz < 0.01 || giris.hiz > 100) {
    sonuc.hatalar.push('Hız 0.01 - 100 m/s arasında olmalı');
    sonuc.gecerli = false;
  }

  if (giris.debi < 10 || giris.debi > 50000) {
    sonuc.hatalar.push('Debi 10 - 50000 particle/s arasında olmalı');
    sonuc.gecerli = false;
  }

  if (giris.sicaklik < -50 || giris.sicaklik > 2000) {
    sonuc.hatalar.push('Sıcaklık -50 - 2000 °C arasında olmalı');
    sonuc.gecerli = false;
  }

  if (giris.debi > 10000) {
    sonuc.uyarilar.push('Yüksek debi performansı etkileyebilir');
  }

  return sonuc;
}

/**
 * Tam simülasyon config'i validate et
 */
export function validateSPHConfig(config: SPHSimulasyonConfig): ValidationSonuc {
  const sonuc: ValidationSonuc = { gecerli: true, hatalar: [], uyarilar: [] };

  // Global validate
  const globalValidation = validateGlobalParametreler(config.global);
  sonuc.hatalar.push(...globalValidation.hatalar);
  sonuc.uyarilar.push(...globalValidation.uyarilar);
  sonuc.gecerli = sonuc.gecerli && globalValidation.gecerli;

  // Girişleri validate
  config.dokumGirisleri.forEach((giris, i) => {
    const girisValidation = validateDokumGirisi(giris);
    girisValidation.hatalar.forEach(hata => {
      sonuc.hatalar.push(`Giriş ${i + 1}: ${hata}`);
    });
    girisValidation.uyarilar.forEach(uyari => {
      sonuc.uyarilar.push(`Giriş ${i + 1}: ${uyari}`);
    });
    sonuc.gecerli = sonuc.gecerli && girisValidation.gecerli;
  });

  // Eleman sayı limitleri
  if (config.dokumGirisleri.length > GPU_STRUCT_BOYUTLARI.MAX_GIRIS) {
    sonuc.hatalar.push(`Maksimum ${GPU_STRUCT_BOYUTLARI.MAX_GIRIS} giriş desteklenir`);
    sonuc.gecerli = false;
  }

  if (config.havaDelikleri.length > GPU_STRUCT_BOYUTLARI.MAX_DELIK) {
    sonuc.hatalar.push(`Maksimum ${GPU_STRUCT_BOYUTLARI.MAX_DELIK} hava deliği desteklenir`);
    sonuc.gecerli = false;
  }

  return sonuc;
}

// ============================================================================
// EXPORT SUMMARY
// ============================================================================

/**
 * Bu dosya export ediyor:
 *
 * ENUM'lar:
 * - SolverTipi, KaliteSeviyesi, AkisProfili, KesitSekli
 * - TurbulansProfili, HavaDeligiTipi, EngelTipi, EngelHareket
 * - CikisTipi, BesleyiciSekil, SensorTipi, TermalTipi
 * - RenderModu, HeatmapTipi, YerlestirmeModu, ParticleFaz
 *
 * INTERFACE'ler:
 * - Vec3, Renk, ParametreTanimi
 * - SPHGlobalParametreler, DFSPHParametreler, PBFParametreler, PCISPHParametreler
 * - TermalParametreler, GazParametreler, RenderParametreler
 * - DokumGirisiConfig, HavaDeligiConfig, BesleyiciConfig, FiltreConfig
 * - EngelConfig, CikisConfig, SanalSensorConfig, TermalElemanConfig
 * - ZamanKeyframe, DokumSenaryo, SPHSimulasyonConfig
 * - KalitePreset, ValidationSonuc
 *
 * CONST'lar:
 * - KALITE_PRESETLERI
 * - GPU_STRUCT_BOYUTLARI
 *
 * FONKSİYONLAR:
 * - hesaplaParticleSayisi, hesaplaParticleBoyutu, tahminGPUBellek
 * - varsayilan* (12 farklı factory fonksiyonu)
 * - vec3, renk, normalizeVec3, cloneVec3, cloneRenk, clampDeger
 * - validate* (3 validasyon fonksiyonu)
 */
